/* eslint-disable camelcase */

import {Duration} from 'luxon';

import {OrNull} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {CirclePublicId} from '../../../models/api/circleModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {WithExpressionRef} from '../expressions/expressionRefModel';
import {StringOrTemplate, StringTemplate} from './stringTemplateModel';

export type TeammateRef = TeammateModel['id'] | CirclePublicId;

export interface TagParam {
  tag?: TagModel['id'];
}

export interface TagsParam {
  tags?: ReadonlyArray<TagModel['id']>;
}

export interface TeammateParam {
  teammate: WithExpressionRef<TeammateRef>;
}

export interface TeammatesParam {
  teammates: ReadonlyArray<WithExpressionRef<TeammateRef>>;
}

export interface InboxParam {
  inbox: InboxModel['id'];
}

export interface InboxesParam {
  inboxes: OrNull<ReadonlyArray<InboxModel['id']>>;
}

export interface ChannelParam {
  channel: ChannelModel['id'];
}

export interface ChannelsParam {
  channels?: ReadonlyArray<ChannelModel['id']>;
}

export interface StringComparatorParam {
  comparator: string;
  text: string;
}

export interface RecipientComparatorParam {
  comparator: string;
  text: StringOrTemplate;
  include_quote?: boolean;
}

export type CommentBodyAndAuthorsParam = Partial<RecipientComparatorParam> & Partial<TeammatesParam>;

export interface CustomAttributeStringParam {
  custom_field: number;
  comparator?: string;
  value?: string;
}

export interface CustomAttributeNumberParam {
  custom_field: number;
  comparator?: string;
  value?: number;
}

export interface CustomAttributeBooleanParam {
  custom_field: number;
  comparator?: string;
}

export interface CustomAttributeDatetimeParam {
  custom_field: number;
  comparator?: string;
  value?: string;
}

export interface CustomAttributeTeammateParam {
  custom_field: number;
  comparator?: string;
  teammate?: number;
}

export interface FieldParam {
  field: string;
}

export interface FieldValueParam {
  field: string;
  value: number | StringTemplate;
}

export interface DaysOfTheWeekParam {
  days: ReadonlyArray<number>;
  timezone: string;
}

export interface TimeRangeParam {
  start_time: string;
  end_time: string;
  timezone: string;
}

export interface DateWithTimezoneParam {
  date: string;
  timezone: string;
}

export interface DateIsParam {
  comparator: string;
  date: DateWithTimezoneParam;
  end_date?: DateWithTimezoneParam;
}

export interface RandomAssignParam {
  algo: 'random' | 'round_robin' | 'load_balancing';
  teammates: ReadonlyArray<WithExpressionRef<TeammateRef>>;
}

export interface CustomFieldParam {
  custom_field: number;
}

export interface MoveTypeParam extends InboxParam {
  moveType: 'restrict' | 'keep';
}

export interface ReplyParam {
  when: 'once' | 'always';
  response?: number;
}

export interface SnoozePresetParam {
  relativeTo: 'minute' | 'hour' | 'day' | 'week';
  offset: {
    minute: number;
    hour: number;
    day: number;
    week: number;
  };
}
export interface SnoozeParam {
  snoozePreset: SnoozePresetParam;
  timezone: string;
}

export interface ForwardParam {
  recipientEmail: string;
}

export enum DelayUnitsEnum {
  MILLISECOND = 'millisecond',
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export interface DelayParameter {
  val: number;
  unit: DelayUnitsEnum;
}

export enum ActivityTypesEnum {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
  INBOUND_OR_OUTBOUND = 'inbound_or_outbound',
}

export interface UnrepliedAfterParam extends DelayParam {
  activity_type?: ActivityTypesEnum;
}

function toLuxonDuration(delay: DelayParameter): Duration {
  return Duration.fromObject({[delay.unit]: delay.val});
}

export const DelayParameter = {
  equals: (a: DelayParameter, b: DelayParameter): boolean => a.val === b.val && a.unit === b.unit,

  greatherThan: (a: DelayParameter, b: DelayParameter): boolean =>
    toLuxonDuration(a).valueOf() > toLuxonDuration(b).valueOf(),
};

// Can be undefined because some legacy rules don't have this parameter.
export enum BusinessHoursParametersEnum {
  REGARDLESS_BH = 'regardless_bh',
  WITHIN_BH = 'within_bh',
}

export interface DelayParam {
  delay?: DelayParameter;
  business_hours?: BusinessHoursParametersEnum;
}

export interface CardGroupParam {
  group: number;
}

export interface CommentBodyParam {
  body: StringTemplate;
}

export type DelayGranularity = 'hours' | 'days';

export type DDHHMM = {
  days?: number;
  hh?: number;
  mm?: number;
};

export function delayToDayHHMM(delay: DelayParameter, granularity: DelayGranularity): DDHHMM {
  const finalUnits = (['days', 'hours', 'minutes'] as const).filter(
    (unit) => unit !== 'days' || granularity === 'days',
  );

  const {
    days,
    hours: hh,
    minutes: mm,
  } = Duration.fromObject({
    [delay.unit]: delay.val,
  }).shiftTo(...finalUnits);

  return {days, hh, mm};
}

// See common_schema / delay.
const MaxDelayValue = 8640000000; // 100 days in ms

/**
 * Converts the day:HH:MM input into a delay.
 * Delays used to have floating precision with a maximum of 1000 "units".
 * Now we want all of them in milliseconds
 * In a future development we will migrate all existing rules to ms delay an simplify the code (getting rid of the 'units')
 */
export function dayhhmmToDelay(dayHHMM: DDHHMM): DelayParameter {
  const {days = 0, hh: hours = 0, mm: minutes = 0} = dayHHMM;

  const durationInMilliseconds = Duration.fromObject({days, hours, minutes}).as('milliseconds');

  if (durationInMilliseconds < MaxDelayValue) {
    return {val: durationInMilliseconds, unit: DelayUnitsEnum.MILLISECOND};
  }

  throw new Error(`Duration of ${JSON.stringify(dayHHMM)} is too large`);
}
